import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  X, Sparkles, Layers, Plus, ArrowRight, RefreshCw, CheckCircle2, AlertCircle,
  Boxes, Grid3x3, Ruler, Calendar, Database, Trash2, ChevronDown, ChevronRight,
} from "lucide-react";
import { Tile, Badge } from "./Primitives";
import {
  listGold, previewGold, createGold, promoteGold, removeGold,
} from "../lib/s3Api";
import { formatNumber, cn } from "../lib/utils";

// Gold Aggregation Studio — dimensional grain builder.
// The operator picks:
//   • one or more Silver tables (promoted through Bronze review) as sources
//   • dimension keys pulled from the intersection of source columns
//     (defaults to branch_id / sku_id / dt / channel — the SOW canonical set)
//   • measures + aggregations (sum / avg / min / max / count)
//   • fact grain: daily / weekly / monthly
// The backend renders a SQL preview + row/storage estimate on every change,
// so the studio feels responsive without executing anything on real S3.

const GRAINS = [
  { key: "daily",   label: "Daily",   note: "One row per (dim…, dt)" },
  { key: "weekly",  label: "Weekly",  note: "DATE_TRUNC('week', dt)" },
  { key: "monthly", label: "Monthly", note: "DATE_TRUNC('month', dt)" },
];
const AGGS = ["sum", "avg", "min", "max", "count"];
// Canonical SOW keys. Others can still be added via free-form input.
const CANONICAL_DIMS = ["branch_id", "sku_id", "dt", "channel"];
const CANONICAL_MEASURES = ["revenue", "qty", "gross_margin", "cogs", "discount"];
// Canonical keys → the standard Silver field names they read from.
const ALIAS = { dt: "date", branch_id: "branch", sku_id: "sku", revenue: "amount", cogs: "unit_cost", discount: "discount_pct" };

export default function GoldAggregationStudio({ open, sources = [], onClose, onSaved }) {
  const promoted = useMemo(() => sources.map(s => ({ ...s, columns: (s.columns || []).map(c => (typeof c === "string" ? { name: c } : c)) })), [sources]);
  const [selected, setSelected] = useState([]); // Silver table ids
  const [dims, setDims] = useState([{ key: "dt" }, { key: "branch_id" }]);
  const [meas, setMeas] = useState([{ key: "qty", agg: "sum" }]);
  const [grain, setGrain] = useState("daily");
  const [name, setName] = useState("Sales Aggregate");
  const [outputTable, setOutputTable] = useState("");
  const [description, setDescription] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [saveErr, setSaveErr] = useState(null);
  const [showSql, setShowSql] = useState(false);

  // Build the union of columns across the selected sources. That's the pool
  // of legal source_column values for dimensions and measures.
  const columnPool = useMemo(() => {
    const seen = new Map(); // name -> { name, type, sources: [] }
    for (const src of promoted) {
      if (!selected.includes(src.id)) continue;
      for (const c of src.columns || []) {
        if (!seen.has(c.name)) seen.set(c.name, { name: c.name, type: c.type, pii: c.pii, sources: [] });
        seen.get(c.name).sources.push(src.id);
      }
    }
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [promoted, selected]);

  const columnNames = useMemo(() => new Set(columnPool.map(c => c.name)), [columnPool]);

  // Resolve which source_id + source_column to use for each dim/measure.
  // Strategy: prefer the first selected source that has the column.
  function resolveColumnRef(key) {
    // 1) exact column-name match, then the standard Silver field for a canonical key
    for (const k of [key, ALIAS[key]].filter(Boolean)) {
      if (columnNames.has(k)) {
        const c = columnPool.find(x => x.name === k);
        return { source_id: c.sources[0], source_column: k };
      }
    }
    // 2) fallback — use first source, first-matching-by-substring column
    for (const src of promoted) {
      if (!selected.includes(src.id)) continue;
      const col = (src.columns || []).find(c => c.name.toLowerCase().includes(key.toLowerCase()));
      if (col) return { source_id: src.id, source_column: col.name };
    }
    // 3) give up — mark unresolved
    return null;
  }

  function payload() {
    const resolvedDims = dims
      .filter(d => d.key)
      .map(d => ({ key: d.key, ...(resolveColumnRef(d.key) || { source_id: selected[0], source_column: d.key }) }));
    const resolvedMeas = meas
      .filter(m => m.key)
      .map(m => ({ key: m.key, agg: m.agg || "sum", ...(resolveColumnRef(m.key) || { source_id: selected[0], source_column: m.key }) }));
    return {
      name: name.trim() || "Gold Aggregate",
      description: description.trim(),
      source_ids: selected,
      dimensions: resolvedDims,
      measures: resolvedMeas,
      grain,
      output_table: outputTable.trim() || undefined,
    };
  }

  // Fetch a preview whenever the config changes (debounced).
  useEffect(() => {
    if (!open) return;
    if (!selected.length || !dims.length || !meas.length) { setPreview(null); return; }
    const p = payload();
    if (!p.dimensions.length || !p.measures.length) { setPreview(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await previewGold({
          name: p.name, source_ids: p.source_ids, dimensions: p.dimensions,
          measures: p.measures, grain: p.grain, output_table: p.output_table,
        });
        setPreview(r);
        setPreviewErr(null);
      } catch (e) {
        setPreview(null);
        setPreviewErr(e.message);
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [open, selected, dims, meas, grain, name, outputTable]);

  useEffect(() => {
    if (!open) return;
    // reset on open
    setSelected([]);
    setDims([{ key: "dt" }, { key: "branch_id" }]);
    setMeas([{ key: "qty", agg: "sum" }]);
    setGrain("daily");
    setName("Sales Aggregate");
    setOutputTable("");
    setDescription("");
    setSavedId(null);
    setSaveErr(null);
    setShowSql(false);
    setPreview(null);
    setPreviewErr(null);
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [open]);

  if (!open) return null;

  function toggleSource(id) {
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  }

  function addDim(key) {
    if (!key || dims.some(d => d.key === key)) return;
    setDims(d => [...d, { key }]);
  }
  function removeDim(key) { setDims(d => d.filter(x => x.key !== key)); }

  function addMeasure(key, agg = "sum") {
    if (!key || meas.some(m => m.key === key)) return;
    setMeas(m => [...m, { key, agg }]);
  }
  function updateMeasureAgg(key, agg) {
    setMeas(m => m.map(x => x.key === key ? { ...x, agg } : x));
  }
  function removeMeasure(key) { setMeas(m => m.filter(x => x.key !== key)); }

  async function handleSave(promoteAfter = false) {
    setSaveErr(null);
    setSaving(true);
    try {
      const p = payload();
      const saved = await createGold(p);
      setSavedId(saved.id);
      if (promoteAfter) {
        const promotedDoc = await promoteGold(saved.id);
        if (onSaved) onSaved(promotedDoc);
      } else if (onSaved) {
        onSaved(saved);
      }
    } catch (e) {
      setSaveErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  const missingCols = [
    ...dims.filter(d => d.key && !resolveColumnRef(d.key)).map(d => d.key),
    ...meas.filter(m => m.key && !resolveColumnRef(m.key)).map(m => m.key),
  ];
  const canSave = selected.length > 0 && dims.length > 0 && meas.length > 0 && !!preview && !savedId && !missingCols.length;

  return (
    <>
      <div data-testid="gold-backdrop" onClick={onClose}
           className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 animate-fade-in" />
      <aside data-testid="gold-studio"
             className="fixed top-0 right-0 h-screen w-full sm:w-[720px] lg:w-[880px] xl:w-[980px] bg-bg border-l border-border shadow-2xl z-50 overflow-y-auto animate-slide-in-right">
        {/* Header */}
        <div className="sticky top-0 bg-bg/95 backdrop-blur-md border-b border-border px-5 py-3.5 flex items-start justify-between gap-4 z-10">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-primary font-semibold mb-1 inline-flex items-center gap-1.5">
              <Sparkles size={11} /> Lakehouse · Gold Aggregation Studio
            </div>
            <h2 className="font-display text-lg font-semibold tracking-tight leading-tight">Compose a Gold fact from Silver tables</h2>
            <p className="text-[12px] text-muted mt-0.5">Multi-source · dimension × measure builder · daily / weekly / monthly grain</p>
          </div>
          <button data-testid="gold-close" onClick={onClose}
                  className="w-8 h-8 shrink-0 inline-flex items-center justify-center border border-border hover:border-primary hover:text-primary rounded-sm">
            <X size={14} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Model identity */}
          <Tile testid="gold-identity" title="Model identity">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Fact name">
                <input data-testid="gold-name" value={name} onChange={e => setName(e.target.value)}
                       className="w-full h-9 px-3 bg-surface-2 border border-border rounded-sm text-sm"
                       placeholder="Sales Aggregate" />
              </Field>
              <Field label="Output table (optional)"
                     hint={preview?.output_table ? `resolves to ${preview.output_table}` : "auto-generated from name + grain"}>
                <input data-testid="gold-output-table" value={outputTable} onChange={e => setOutputTable(e.target.value)}
                       className="w-full h-9 px-3 bg-surface-2 border border-border rounded-sm text-sm font-mono-tabular"
                       placeholder="e.g. fact_sales_daily" />
              </Field>
              <Field label="Description" className="md:col-span-2">
                <textarea data-testid="gold-description" rows={2} value={description}
                          onChange={e => setDescription(e.target.value)}
                          className="w-full px-3 py-2 bg-surface-2 border border-border rounded-sm text-sm resize-none"
                          placeholder="What is this Gold fact used for?" />
              </Field>
            </div>
          </Tile>

          {/* Sources */}
          <Tile testid="gold-sources"
                title={`Silver sources · ${selected.length} selected`}
                action={<Badge status={selected.length ? "success" : "warning"}>{promoted.length} Silver tables available</Badge>}>
            {promoted.length === 0 ? (
              <div className="p-4 text-center border border-dashed border-border rounded-sm text-[12px] text-muted">
                No Silver tables yet. Review a Bronze item and promote it to Silver in <span className="text-primary font-medium">Data Management → Bronze</span> first.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {promoted.map(a => {
                  const sel = selected.includes(a.id);
                  return (
                    <label key={a.id} data-testid={`gold-source-${a.id}`}
                           className={cn("p-2.5 border rounded-sm cursor-pointer transition-colors flex items-start gap-2",
                             sel ? "border-primary bg-primary/5" : "border-border bg-surface-2/40 hover:bg-surface-2")}>
                      <input type="checkbox" checked={sel} onChange={() => toggleSource(a.id)}
                             className="mt-1 accent-primary shrink-0"
                             data-testid={`gold-source-cb-${a.id}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-medium truncate">{a.label}</div>
                        <div className="text-[10px] text-muted font-mono-tabular truncate">silver.{a.table}</div>
                        <div className="flex items-center gap-2 text-[10px] text-muted mt-1 flex-wrap font-mono-tabular">
                          <Badge status={a.stage === "gold" ? "success" : "neutral"}>{a.stage}</Badge>
                          <span>{formatNumber(a.rows, { compact: true })} rows</span>
                          <span>· {(a.columns || []).length} cols</span>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </Tile>

          {/* Dimensions */}
          <Tile testid="gold-dimensions"
                title="Dimensions"
                action={<Badge status="info"><Grid3x3 size={10} className="inline mr-1" />{dims.length} keys</Badge>}>
            <div className="space-y-2">
              <div className="flex flex-wrap gap-1.5">
                {dims.map(d => (
                  <Chip key={d.key} label={d.key} onRemove={() => removeDim(d.key)}
                        testid={`gold-dim-${d.key}`}
                        warn={!resolveColumnRef(d.key)} />
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] uppercase tracking-widest text-muted font-semibold">SOW canonical:</span>
                {CANONICAL_DIMS.filter(k => !dims.some(d => d.key === k)).map(k => (
                  <button key={k} onClick={() => addDim(k)}
                          data-testid={`gold-dim-add-${k}`}
                          className="px-2 h-6 text-[11px] border border-border hover:border-primary hover:text-primary rounded-sm inline-flex items-center gap-1 font-mono-tabular">
                    <Plus size={10} /> {k}
                  </button>
                ))}
              </div>
              {columnPool.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <span className="text-[10px] uppercase tracking-widest text-muted font-semibold">From selected sources:</span>
                  {columnPool.slice(0, 12).filter(c => !dims.some(d => d.key === c.name) && !CANONICAL_DIMS.includes(c.name)).slice(0, 8).map(c => (
                    <button key={c.name} onClick={() => addDim(c.name)}
                            data-testid={`gold-dim-add-col-${c.name}`}
                            className="px-2 h-6 text-[11px] border border-border hover:border-primary hover:text-primary rounded-sm inline-flex items-center gap-1 font-mono-tabular">
                      <Plus size={10} /> {c.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </Tile>

          {/* Measures */}
          <Tile testid="gold-measures"
                title="Measures"
                action={<Badge status="info"><Ruler size={10} className="inline mr-1" />{meas.length} metrics</Badge>}>
            <div className="space-y-2">
              <div className="space-y-1.5">
                {meas.map(m => (
                  <div key={m.key} className="flex items-center gap-2 p-1.5 border border-border rounded-sm bg-surface-2/40"
                       data-testid={`gold-measure-${m.key}`}>
                    <Badge status={resolveColumnRef(m.key) ? "info" : "warning"}>
                      {resolveColumnRef(m.key) ? "resolved" : "unresolved"}
                    </Badge>
                    <span className="font-mono-tabular text-sm text-primary flex-1">{m.key}</span>
                    <select value={m.agg}
                            onChange={e => updateMeasureAgg(m.key, e.target.value)}
                            data-testid={`gold-measure-agg-${m.key}`}
                            className="h-7 px-2 bg-bg border border-border rounded-sm text-[11px] font-mono-tabular uppercase">
                      {AGGS.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <button data-testid={`gold-measure-remove-${m.key}`} onClick={() => removeMeasure(m.key)}
                            className="w-6 h-6 flex items-center justify-center border border-border hover:border-critical hover:text-critical rounded-sm">
                      <Trash2 size={10} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] uppercase tracking-widest text-muted font-semibold">SOW canonical:</span>
                {CANONICAL_MEASURES.filter(k => !meas.some(m => m.key === k)).map(k => (
                  <button key={k} onClick={() => addMeasure(k)}
                          data-testid={`gold-measure-add-${k}`}
                          className="px-2 h-6 text-[11px] border border-border hover:border-primary hover:text-primary rounded-sm inline-flex items-center gap-1 font-mono-tabular">
                    <Plus size={10} /> {k}
                  </button>
                ))}
              </div>
            </div>
          </Tile>

          {/* Grain + estimate */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Tile testid="gold-grain" title="Fact grain">
              <div className="grid grid-cols-3 gap-2">
                {GRAINS.map(g => {
                  const active = grain === g.key;
                  return (
                    <button key={g.key} onClick={() => setGrain(g.key)} data-testid={`gold-grain-${g.key}`}
                            className={cn("p-2.5 border rounded-sm text-left transition-colors",
                              active ? "border-primary bg-primary/5" : "border-border bg-surface-2/40 hover:bg-surface-2")}>
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <Calendar size={11} className={active ? "text-primary" : "text-muted"} />
                        <span className={cn("text-sm font-semibold", active && "text-primary")}>{g.label}</span>
                      </div>
                      <div className="text-[10px] text-muted font-mono-tabular">{g.note}</div>
                    </button>
                  );
                })}
              </div>
            </Tile>

            <Tile testid="gold-estimate" title="Estimated output"
                  action={previewErr && <Badge status="critical">{previewErr.slice(0, 40)}</Badge>}>
              <div className="grid grid-cols-3 divide-x divide-border">
                <MiniStat label="Fact table" value={preview?.output_table || "…"} mono />
                <MiniStat label="Rows"
                          value={preview ? formatNumber(preview.est_rows, { compact: true }) : "…"} />
                <MiniStat label="Storage"
                          value={preview ? `${preview.est_size_mb} MB` : "…"} />
              </div>
              {missingCols.length > 0 && (
                <div className="mt-2 text-[10.5px] text-warning inline-flex items-center gap-1.5" data-testid="gold-missing-cols">
                  <AlertCircle size={11} /> Missing columns in current sources: {missingCols.join(", ")}
                </div>
              )}
            </Tile>
          </div>

          {/* SQL preview */}
          <Tile testid="gold-sql-tile"
                title="SQL preview"
                action={
                  <button data-testid="gold-sql-toggle" onClick={() => setShowSql(s => !s)}
                          className="px-2 h-6 text-[10.5px] border border-border hover:border-primary hover:text-primary rounded-sm inline-flex items-center gap-1">
                    {showSql ? <ChevronDown size={10} /> : <ChevronRight size={10} />} {showSql ? "Hide" : "Show"} SQL
                  </button>
                }>
            {showSql && (
              <pre className="text-[10.5px] font-mono-tabular leading-snug bg-surface-2/60 border border-border rounded-sm p-3 overflow-x-auto whitespace-pre max-h-[260px]" data-testid="gold-sql-pre">
{preview?.sql_preview || "// select at least one source, dimension and measure"}
              </pre>
            )}
            {!showSql && (
              <div className="text-[11px] text-muted">
                Backend renders a dbt-style CREATE OR REPLACE TABLE statement on every change.
              </div>
            )}
          </Tile>

          {saveErr && (
            <div className="p-2.5 border border-critical/40 bg-critical/5 rounded-sm text-[12px] text-critical inline-flex items-center gap-2" data-testid="gold-save-error">
              <AlertCircle size={12} /> {saveErr}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="sticky bottom-0 bg-bg/95 backdrop-blur-md border-t border-border px-5 py-3 flex items-center justify-between gap-3 z-10">
          <div className="text-[11px] text-muted min-w-0 truncate">
            {savedId ? (
              <span className="text-success inline-flex items-center gap-1"><CheckCircle2 size={11} /> Saved · {savedId}</span>
            ) : preview && missingCols.length ? (
              <span className="text-warning" data-testid="gold-footer-missing">Remove or replace {missingCols.join(", ")} — not a column of the selected Silver tables.</span>
            ) : preview ? (
              <span>Ready to promote — grain <span className="text-primary font-mono-tabular">{grain}</span> · <span className="text-primary font-mono-tabular">{preview.output_table}</span></span>
            ) : (
              <span>Pick sources, dimensions, and measures to see the estimate.</span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button data-testid="gold-save-draft" disabled={!canSave || saving}
                    onClick={() => handleSave(false)}
                    className="px-3 py-1.5 text-xs border border-primary text-primary hover:bg-primary hover:text-primary-fg rounded-sm disabled:opacity-40 inline-flex items-center gap-1.5 transition-colors">
              {saving && <RefreshCw size={11} className="animate-spin" />}
              <Layers size={11} /> Save as staged
            </button>
            <button data-testid="gold-save-promote" disabled={!canSave || saving}
                    onClick={() => handleSave(true)}
                    className="px-3 py-1.5 text-xs bg-primary text-primary-fg hover:opacity-90 font-semibold rounded-sm disabled:opacity-40 inline-flex items-center gap-1.5">
              {saving && <RefreshCw size={11} className="animate-spin" />}
              <CheckCircle2 size={11} /> Promote to Gold
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

function Field({ label, hint, children, className }) {
  return (
    <div className={cn("space-y-1", className)}>
      <label className="text-[10px] uppercase tracking-widest text-muted font-semibold">{label}</label>
      {children}
      {hint && <div className="text-[10px] text-subtle">{hint}</div>}
    </div>
  );
}

function Chip({ label, onRemove, testid, warn }) {
  return (
    <span data-testid={testid}
          className={cn("inline-flex items-center gap-1 px-2 h-6 rounded-sm text-[11px] font-mono-tabular border",
            warn ? "border-warning/50 bg-warning/5 text-warning" : "border-primary/50 bg-primary/10 text-primary")}>
      {label}
      <button onClick={onRemove}
              data-testid={`${testid}-remove`}
              className="ml-0.5 opacity-70 hover:opacity-100">
        <X size={10} />
      </button>
    </span>
  );
}

function MiniStat({ label, value, mono }) {
  return (
    <div className="px-3 py-1.5 min-w-0">
      <div className="text-[9px] uppercase tracking-widest text-muted font-semibold">{label}</div>
      <div title={String(value)} className={cn("font-display font-semibold mt-0.5 truncate", mono ? "text-[13px] leading-7 font-mono-tabular text-primary" : "text-lg")}>{value}</div>
    </div>
  );
}
